import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, ArrowUpRight, ChevronRight, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import type { DatasetColumn, DatasetRow, MetricKey, Scope } from "@busright/data-contract";

import { EmptyState } from "@/components/EmptyState";
import { SkeletonLines } from "@/components/Skeleton";
import type { DrillFrame } from "@/context/DrillContext";
import { useDataset } from "@/hooks/queries";
import { formatMetricValue } from "@/lib/format";
import { getMetricDef } from "@/lib/metric-registry";
import { scopeDisplayName, scopeKey } from "@/lib/scope";

// =================================================================================================
// DatasetDrawer — the drill engine's surface. Renders a `DatasetResponse` as a sortable table where
// any row with a `drillScope` drills one grain deeper. The drawer owns an in-drawer BREADCRUMB STACK
// of frames ({metricKey, scope}); drilling a row pushes a frame, the breadcrumb pops back up. The
// root frame comes from DrillContext. Built on Radix Dialog (focus trap, Escape, outside-click) and
// presented as a right-side panel.
//
// Honesty: the value column uses the metric's unit formatter; the drawer header shows the metric's
// definition. When the `/api/dataset` endpoint isn't live, the query errors and we render an error
// state — never a fabricated table.
// =================================================================================================

type SortDir = "asc" | "desc";

interface SortState {
  readonly key: string;
  readonly dir: SortDir;
}

// One step of the breadcrumb stack.
interface StackFrame {
  readonly metricKey: MetricKey;
  readonly scope: Scope;
}

const compareCells = (a: DatasetRow, b: DatasetRow, key: string, dir: SortDir): number => {
  const av = a.cells[key];
  const bv = b.cells[key];
  // Nulls sort last regardless of direction.
  if (av === null || av === undefined) {
    return bv === null || bv === undefined ? 0 : 1;
  }
  if (bv === null || bv === undefined) {
    return -1;
  }
  let cmp: number;
  if (typeof av === "number" && typeof bv === "number") {
    cmp = av - bv;
  } else {
    cmp = String(av).localeCompare(String(bv));
  }
  return dir === "asc" ? cmp : -cmp;
};

interface DrawerBodyProps {
  readonly frame: StackFrame;
  readonly onDrill: (metricKey: MetricKey, scope: Scope) => void;
}

// The table for one frame: fetches the dataset for {metricKey, scope}, renders a sortable table,
// and drills a row (push a frame) when it carries a drillScope.
const DrawerBody = ({ frame, onDrill }: DrawerBodyProps): ReactNode => {
  const { data, isLoading, isError, error } = useDataset({
    metricKey: frame.metricKey,
    scope: frame.scope,
  });
  const [sort, setSort] = useState<SortState | null>(null);

  // Reset sort when the frame changes (different metric/scope = different columns).
  useEffect(() => {
    setSort(null);
  }, []);

  const payload = data;
  const columns: readonly DatasetColumn[] = payload?.columns ?? [];

  const sortedRows = useMemo(() => {
    const rows = payload?.rows ?? [];
    if (!sort) {
      return rows;
    }
    return [...rows].sort((a, b) => compareCells(a, b, sort.key, sort.dir));
  }, [payload?.rows, sort]);

  const toggleSort = (key: string): void => {
    setSort((prev) => {
      if (!prev || prev.key !== key) {
        return { key, dir: "desc" };
      }
      return { key, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };

  if (isLoading) {
    return <SkeletonLines count={6} />;
  }
  if (isError) {
    return (
      <EmptyState
        title="Couldn't load this dataset"
        message={
          error instanceof Error
            ? `${error.message} — the /api/dataset endpoint may not be live yet.`
            : "The /api/dataset endpoint may not be live yet."
        }
        icon={AlertTriangle}
      />
    );
  }
  if (!payload || sortedRows.length === 0) {
    return <EmptyState title="No rows" message="Nothing behind this metric at this scope." />;
  }

  const childLabel = payload.childLevel ? `by ${payload.childLevel}` : "rows";

  return (
    <div className="drawer-dataset">
      <div className="drawer-dataset-meta br-tabular-nums">
        {payload.total != null
          ? `${sortedRows.length} of ${payload.total} ${childLabel}`
          : `${sortedRows.length} ${childLabel}`}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key} className={col.align === "right" ? "num" : undefined}>
                  <button
                    type="button"
                    className="drawer-sort"
                    onClick={() => toggleSort(col.key)}
                    aria-label={`Sort by ${col.label}`}
                  >
                    {col.label}
                    {sort?.key === col.key ? (
                      <span aria-hidden>{sort.dir === "asc" ? " ▲" : " ▼"}</span>
                    ) : null}
                  </button>
                </th>
              ))}
              <th aria-label="Drill" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const drillTo = row.drillScope;
              return (
                <tr
                  key={row.id}
                  className={drillTo ? "drawer-row-drillable" : undefined}
                  onClick={drillTo ? () => onDrill(frame.metricKey, drillTo) : undefined}
                  style={drillTo ? { cursor: "pointer" } : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={col.align === "right" ? "num" : undefined}>
                      {formatMetricValue(row.cells[col.key] ?? null, col.unit)}
                    </td>
                  ))}
                  <td className="num">{drillTo ? <ArrowUpRight size={13} aria-hidden /> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export interface DatasetDrawerProps {
  // The root frame (from DrillContext). Null when closed.
  readonly root: DrillFrame | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

export const DatasetDrawer = ({ root, open, onClose }: DatasetDrawerProps): ReactNode => {
  // The breadcrumb stack: index 0 is the root frame, each drill pushes one.
  const [stack, setStack] = useState<readonly StackFrame[]>([]);

  // Seed the stack from the root frame each time the drawer opens at a new root.
  useEffect(() => {
    if (root) {
      setStack([{ metricKey: root.metricKey, scope: root.scope }]);
    } else {
      setStack([]);
    }
  }, [root]);

  const pushFrame = (metricKey: MetricKey, scope: Scope): void => {
    setStack((prev) => [...prev, { metricKey, scope }]);
  };

  // Truncate the stack back to (and including) index `i` — the breadcrumb click target.
  const popTo = (i: number): void => {
    setStack((prev) => prev.slice(0, i + 1));
  };

  const current = stack[stack.length - 1] ?? null;
  const def = current ? getMetricDef(current.metricKey) : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="drawer" aria-describedby={undefined}>
          <div className="drawer-head">
            <div className="drawer-titles">
              <Dialog.Title className="drawer-title">
                {def?.label ?? current?.metricKey ?? "Dataset"}
              </Dialog.Title>
              {def ? <p className="drawer-subtitle">{def.tooltip}</p> : null}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="drawer-close" aria-label="Close dataset drawer">
                <X size={16} aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          {stack.length > 0 ? (
            <nav className="drawer-breadcrumbs" aria-label="Drill breadcrumb">
              {stack.map((f, i) => (
                <span key={`${f.metricKey}-${scopeKey(f.scope)}`} className="drawer-crumb">
                  {i > 0 ? <ChevronRight size={12} aria-hidden /> : null}
                  <button
                    type="button"
                    className="drawer-crumb-btn"
                    disabled={i === stack.length - 1}
                    onClick={() => popTo(i)}
                  >
                    {scopeDisplayName(f.scope)}
                  </button>
                </span>
              ))}
            </nav>
          ) : null}

          <div className="drawer-scroll">
            {current ? <DrawerBody frame={current} onDrill={pushFrame} /> : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
